import { filterVisibleCategories } from '@/lib/tasks/visibleCategories';
/**
 * =============================================================================
 * NavTaskCategoriesTree — Atalhos de categorias dos Kanbans de tarefas
 * Versão: 1.3.0 (2026-10-10)
 *
 * Descrição:
 *   Lista as categorias ativas de Tarefas, Retenção ou Jurídico no painel
 *   lateral e abre o Kanban diretamente na categoria escolhida. Reutiliza o
 *   hook oficial para manter as regras iguais às abas do Kanban.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-10 v1.3.0 - Reutiliza a mesma árvore para as categorias
 *            do Kanban de Ordem de Serviço, sem criar catálogo paralelo.
 *   RELEASE: 2026-10-05 v1.2.0 - Generaliza tipo, rota e preferência de
 *            expansão para atender Tarefas, Retenção e Jurídico sem duplicação.
 *   RELEASE: 2026-10-05 v1.1.0 - Adiciona controle persistente para retrair ou
 *            expandir as categorias, mantendo aberta a categoria ativa.
 *   RELEASE: 2026-10-05 v1.0.0 - Versão inicial com link geral, categorias
 *            dinâmicas, destaque pela URL e carregamento discreto.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Kanban } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { SERVICE_ORDER_MODULE, TASK_MODULES, type TaskModuleType } from '@/hooks/useTaskModuleType';
import { cn } from '@/lib/utils';

interface NavTaskCategoriesTreeProps {
  /** Tipo que define o catálogo e a rota do Kanban exibido. */
  taskType: TaskModuleType;
  /** Usa o catálogo e a rota próprios da Ordem de Serviço. */
  serviceOrders?: boolean;
  /** Callback após navegar, usado para fechar o painel no mobile. */
  onNavigate?: (url?: string) => void;
}

export function NavTaskCategoriesTree({ taskType, serviceOrders = false, onNavigate }: NavTaskCategoriesTreeProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  // O override garante o catálogo da O.S. mesmo quando o painel permanece fixado após navegar.
  const { categories: allCategories, isLoading } = useTaskCategories(true, serviceOrders ? undefined : taskType, serviceOrders);
  // 2026-10-22: mesmo filtro do Kanban (atalho não leva a abas ocultas)
  const categories = filterVisibleCategories(allCategories);
  const moduleMeta = serviceOrders ? SERVICE_ORDER_MODULE : TASK_MODULES[taskType];
  const kanbanPath = `${moduleMeta.basePath}/kanban`;
  // Preserva a chave já usada por Tarefas e separa as preferências dos demais módulos.
  const expandedStorageKey = serviceOrders
    ? 'service-orders-kanban-categories-expanded'
    : taskType === 'normal'
    ? 'tasks-kanban-categories-expanded'
    : `${taskType}-kanban-categories-expanded`;

  // Mantém a preferência de exibição entre acessos; por padrão a lista inicia aberta.
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      return localStorage.getItem(expandedStorageKey) !== 'false';
    } catch {
      return true;
    }
  });

  // A categoria ativa é sempre identificada pela URL compartilhada com o Kanban.
  const activeCategoryId = pathname === kanbanPath ? searchParams.get('category') : null;
  const isGeneralActive = pathname === kanbanPath && !activeCategoryId;

  // Ao acessar uma categoria diretamente, garante que o atalho ativo permaneça visível.
  useEffect(() => {
    if (activeCategoryId) setIsExpanded(true);
  }, [activeCategoryId]);

  // Persiste apenas a preferência manual de expansão deste submenu.
  useEffect(() => {
    try {
      localStorage.setItem(expandedStorageKey, String(isExpanded));
    } catch {
      /* localStorage indisponível — mantém apenas o estado da sessão atual. */
    }
  }, [expandedStorageKey, isExpanded]);

  /** Navega sem recarregar e preserva o fechamento esperado no painel mobile. */
  const openKanban = (categoryId?: string) => {
    const url = categoryId
      ? `${kanbanPath}?category=${encodeURIComponent(categoryId)}`
      : kanbanPath;
    navigate(url);
    onNavigate?.(url);
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex w-full items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          onClick={() => openKanban()}
          className={cn(
            'h-8 min-w-0 flex-1 justify-start gap-2 px-2 text-sm font-normal',
            'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            isGeneralActive && 'bg-sidebar-primary/10 font-medium text-sidebar-primary',
          )}
        >
          <Kanban className="h-4 w-4 shrink-0" />
          <span className="truncate">Kanban</span>
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={isExpanded ? 'Recolher categorias' : 'Expandir categorias'}
          aria-expanded={isExpanded}
          title={isExpanded ? 'Recolher categorias' : 'Expandir categorias'}
          onClick={() => setIsExpanded((current) => !current)}
          className="h-8 w-8 shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {isLoading && isExpanded && (
        <div className="space-y-1 pl-5">
          {[1, 2].map((item) => (
            <Skeleton key={item} className="h-7 w-full rounded" />
          ))}
        </div>
      )}

      {!isLoading && isExpanded && categories.length > 0 && (
        <div className="flex flex-col gap-0.5 pl-5">
          {categories.map((category) => {
            const isActive = category.id === activeCategoryId;

            return (
              <Button
                key={category.id}
                type="button"
                variant="ghost"
                onClick={() => openKanban(category.id)}
                className={cn(
                  'h-8 w-full justify-start gap-2 px-2 text-sm font-normal',
                  'text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  isActive && 'bg-sidebar-primary/10 font-medium text-sidebar-primary',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: category.color }}
                />
                <span className="truncate">{category.name}</span>
                {category.is_default && (
                  <span className="ml-auto shrink-0 text-[10px] uppercase text-sidebar-foreground/50">
                    padrão
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}